import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Checkbox } from '@atlaskit/checkbox';
import Textfield from '@atlaskit/textfield';
import { FormValues } from '../../../types/ChampionApplicationFormTypes';

type FieldErrors = {
  products?: string;
  otherAtlassianProducts?: string;
};

type Props = {
  values: FormValues;
  updateMultiSelect: (key: keyof FormValues, optionId: string) => void;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

const PRODUCT_OPTIONS = [
  { id: 'ai_rovo', label: 'Atlassian Intelligence/Rovo' },
  { id: 'atlassian_guard', label: 'Atlassian Guard' },
  { id: 'compass', label: 'Compass' },
  { id: 'confluence', label: 'Confluence' },
  { id: 'enterprise', label: 'Enterprise' },
  { id: 'forge', label: 'Forge' },
  { id: 'jira', label: 'Jira' },
  { id: 'jira_align', label: 'Jira Align' },
  { id: 'jira_product_discovery', label: 'Jira Product Discovery' },
  { id: 'jira_service_management', label: 'Jira Service Management' },
  { id: 'loom', label: 'Loom' },
  { id: 'marketplace_apps', label: 'Marketplace apps' },
  { id: 'trello', label: 'Trello' },
  { id: 'other', label: 'Other (please specify)' },
];

export const ProductsSection: React.FC<Props> = ({ values, updateMultiSelect, updateField, errors }) => {
  const toggleProduct = (id: string) => () => updateMultiSelect('products', id);
  const showOtherInput = values.products.includes('other');

  return (
    <fieldset css={sectionBoxStyles}>
      <legend css={legendLabelStyles}>
        <span css={requiredStyles}>*</span>{' '}
        Which Atlassian apps do you use? Select all that apply.
      </legend>

      <div css={controlTopStyles}>
        {PRODUCT_OPTIONS.map((opt) => (
          <Checkbox
            key={opt.id}
            name="productsused"
            label={opt.label}
            isChecked={values.products.includes(opt.id)}
            onChange={toggleProduct(opt.id)}
          />
        ))}

        {errors?.products && <div css={errorStyles}>{errors.products}</div>}
      </div>

      {showOtherInput && (
        <div css={fieldTopStyles}>
          <label css={labelStyles} htmlFor="other-atlassian-products">
            Please specify
          </label>
          <Textfield
            id="other-atlassian-products"
            value={values.otherAtlassianProducts}
            onChange={(e) => updateField('otherAtlassianProducts', (e.target as HTMLInputElement).value)}
          />
          {errors?.otherAtlassianProducts && <div css={errorStyles}>{errors.otherAtlassianProducts}</div>}
        </div>
      )}
    </fieldset>
  );
};

const sectionBoxStyles = css({
  border: `1px solid ${token('color.border')}`,
  borderRadius: '4px',
  padding: token('space.300'),
  marginBottom: token('space.400'),
  backgroundColor: token('elevation.surface'),
});

const legendLabelStyles = css({
  fontWeight: 600,
  fontSize: '14px',
});

const controlTopStyles = css({
  marginTop: token('space.100'),
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.200'),
});

const labelStyles = css({
  fontWeight: 600,
  fontSize: '14px',
  marginBottom: token('space.050'),
});

const requiredStyles = css({
  color: token('color.text.danger'),
});

const errorStyles = css({
  color: token('color.text.danger'),
  fontSize: '12px',
  marginTop: token('space.050'),
});
